import { useEffect, useRef } from "react";
import * as d3 from "d3";
import defaultParams from "config";
import { useGame } from "context/GameContext";
import Debug from "debug";
const debug = Debug("pages:Map");

function makeD3Path(path) {
  let p = d3.path();
  p.moveTo(1000 * path[0][0], 1000 * path[0][1]);
  for (let i = 1; i < path.length; i++) {
    p.lineTo(1000 * path[i][0], 1000 * path[i][1]);
  }
  return p.toString();
}

function drawPaths(svg, cls, pathData) {
  let paths = svg.selectAll("path." + cls).data(pathData);
  paths.enter().append("path").classed(cls, true);
  paths.exit().remove();
  svg.selectAll("path." + cls).attr("d", makeD3Path);
}

function visualizeSlopes(svg, strokes) {
  let lines = svg.selectAll("line.slope").data(strokes);
  lines.enter().append("line").classed("slope", true);
  lines.exit().remove();
  svg
    .selectAll("line.slope")
    .attr("x1", function (d) {
      return 1000 * d[0][0];
    })
    .attr("y1", function (d) {
      return 1000 * d[0][1];
    })
    .attr("x2", function (d) {
      return 1000 * d[1][0];
    })
    .attr("y2", function (d) {
      return 1000 * d[1][1];
    });
}

function visualizeCities(svg, render) {
  let cities = render.cities;
  let h = render.h;
  let n = render.params.nterrs;

  debug(cities);
  let circs = svg.selectAll("circle.city").data(cities);
  circs.enter().append("circle").classed("city", true);
  circs.exit().remove();
  svg
    .selectAll("circle.city")
    .attr("cx", function (d) {
      return 1000 * h.mesh.vxs[d][0];
    })
    .attr("cy", function (d) {
      return 1000 * h.mesh.vxs[d][1];
    })
    .attr("r", function (d, i) {
      return i >= n ? 4 : 10;
    })
    .style("fill", "white")
    .style("stroke-width", 5)
    .style("stroke-linecap", "round")
    .style("stroke", "black")
    .raise();
}

const drawCities = (svg, labels) => {
  let texts = svg.selectAll("text.city").data(labels);
  texts.enter().append("text").classed("city", true);
  texts.exit().remove();
  svg
    .selectAll("text.city")
    .attr("x", function (d) {
      return 1000 * d.x;
    })
    .attr("y", function (d) {
      return 1000 * d.y;
    })
    .style("font-size", function (d) {
      return d.size;
    })
    .style("text-anchor", function (d) {
      return d.align;
    })
    .text(function (d) {
      return d.text;
    })
    .raise();
};

const drawRegions = (svg, labels) => {
  let texts = svg.selectAll("text.region").data(labels);
  texts.enter().append("text").classed("region", true);
  texts.exit().remove();
  svg
    .selectAll("text.region")
    .attr("x", function (d) {
      return 1000 * d.x;
    })
    .attr("y", function (d) {
      return 1000 * d.y;
    })
    .style("font-size", function (d) {
      return 1000 * d.size;
    })
    .style("text-anchor", "middle")
    .text(function (d) {
      return d.text;
    })
    .raise();
};

function drawMap(svg, render) {
  drawPaths(svg, "river", render.rivers.paths);
  drawPaths(svg, "coast", render.coasts);
  drawPaths(svg, "border", render.borders);
  drawPaths(svg, "road", render.roads);
  visualizeSlopes(svg, render.slopes);
  visualizeCities(svg, render);

  svg.selectAll("circle.icon").raise();

  drawCities(svg, render.cityLabels);
  drawRegions(svg, render.regionLabels);
}

function doMap(svg, params, render) {
  let width = svg.attr("width");
  svg.attr("height", (width * params.extent.height) / params.extent.width);
  svg.attr(
    "viewBox",
    (-1000 * params.extent.width) / 2 +
      " " +
      (-1000 * params.extent.height) / 2 +
      " " +
      1000 * params.extent.width +
      " " +
      1000 * params.extent.height,
  );
  svg.selectAll().remove();

  drawMap(svg, render);

  return render;
}

const RoadMap = ({ map, className, children }) => {
  const { gameState } = useGame();
  const { political, notifications } = gameState;
  const ref = useRef();

  useEffect(() => {
    if (!map || !political) return;

    // Assuming `getControlledCities` returns an array of city indices or identifiers
    // that are currently controlled.
    const controlledCities = political.getCitiesControlled().map((city) =>
      city.position
    );
    if (controlledCities.length === 0) return;
    debug(controlledCities);

    const svgElement = d3.select(ref.current);

    // Update the fill color of city circles based on control status
    svgElement.selectAll("circle.city").style("fill", function (d, i) {
      // Assuming `d` or `i` can be used to determine if the city is controlled.
      // Adjust the condition based on your data structure.
      return controlledCities.includes(d) ? "green" : "white";
    });
  }, [map, political, notifications]);

  useEffect(() => {
    if (!map) return;

    const svgElement = d3.select(ref.current);
    doMap(svgElement, defaultParams, map);
  }, [map]);

  return (
    <svg
      className={className}
      viewBox="0 0 500 500"
      ref={ref}
      height={800}
      width={600}
    >
      {children}
    </svg>
  );
};

export default RoadMap;
